import { LoginCallback, Security } from '@okta/okta-react'
import { OktaAuth, toRelativeUrl } from '@okta/okta-auth-js'
import { cacheManager, removeAppCookies, Static_Pages } from 'helpers/util-common'
import React from 'react'
import { Route } from 'react-router-dom'
import { useHistory } from 'react-router-dom'
import { IDENTITY_DELEGATION_FLAG, SESSION_STORAGE_KEYS, USER_INFO } from 'models/common.models'
import { get } from "lodash";
import { useDispatch } from 'react-redux'
import { setFeatureFlag } from 'common.actions'

// Flag For child application.
export let PROTACTED_HOME_PAGE = process.env.REACT_APP_MEETING_BASE_URL

// Setup Configuration for OktaAuth. 
export const OIDC = new OktaAuth({
	issuer: process.env.REACT_APP_OKTA_ISSUER,
	clientId: process.env.REACT_APP_OKTA_CLIENT_ID,
	redirectUri: process.env.REACT_APP_REDIRECT_URI,
	scopes: ['openid', 'profile', 'email'],
	pkce: true, // [true] provides a modern solution for protecting SPAs. 
	postLogoutRedirectUri: process.env.REACT_APP_LOGOUT_URI,
	disableHttpsCheck: window.location.hostname !== 'localhost' ? true : false, // [True] if working on https environment.
	tokenManager: {
		expireEarlySeconds: 60,
		autoRenew: true, // autorenew [true] auto update access token
		secure: window.location.hostname !== 'localhost' ? true : false, // This option is only relevant if storage is set to cookie and false option is only for testing
	},
	services: {
	  autoRenew: true,
	  autoRemove: true
	}
})

export const isStaticPageHandler = (location) => {
	const pathName = location ? location.pathname.toLowerCase() : window.location.pathname;
	const normalizedTargetUrl = pathName ? pathName.replace(/\/+$/, '') : "";
	if (Static_Pages.includes(normalizedTargetUrl)) {
		return normalizedTargetUrl
	}
	return false
}

export const handleLoggedIn = async (redirectURL = '') => {
	//Manage static page redirection
    let redirectCallbackURL = '';
	const isStaticPage = isStaticPageHandler();
	if (isStaticPage) {
		setPageRedirectUrl();
		sessionStorage.setItem(SESSION_STORAGE_KEYS.STATIC_REDIRECT_URL, true);
		redirectCallbackURL = `?callbackUrl=${window.location.pathname + window.location.search}`
	}
    if (redirectURL) {
        redirectCallbackURL += `?callbackUrl=${redirectURL}`
    }
    const genratedRedirectURL = process.env.REACT_APP_LOGIN_PAGE_REQUEST + window.btoa(window.location.origin + redirectCallbackURL);
    window.location.assign(genratedRedirectURL);
}

// Logout Handler
export const logoutHandler = async () => {
    window.localStorage.setItem('isMemberStillLoginInAnotherTab', false);
    try {
        OIDC.signOut('/');
    } catch (error) {
        console.error(error);
    }
}

export const redirectUrlHandler = () => {
    const redirectUrl = sessionStorage.getItem("REDIRECT_URL");
    return redirectUrl || null;
}

export const setPageRedirectUrl = (pathname = window.location.pathname, redirectUrlKey) => {
    const currentPathname = pathname + window.location.search;
    sessionStorage.setItem(redirectUrlKey ? redirectUrlKey : SESSION_STORAGE_KEYS.REDIRECT_CALLBACK_URL, currentPathname);
};

export const oktaWithRedirectHandler = async (isLoginRequired = false) => {
	const isSessionExist = await OIDC.session.exists();
	if (!isSessionExist && isLoginRequired) {
		const callbackUrl = redirectUrlHandler();
		if (callbackUrl) {
			handleLoggedIn(callbackUrl);
		} else {
			handleLoggedIn(window.location.pathname + window.location.search);
		}
	} else if (isSessionExist) {
		setTimeout(() => {
			const idToken = OIDC.getIdToken();
			if (!idToken) {
				OIDC.token.getWithRedirect({
					scopes: OIDC.options.scopes
				});
			}
		}, 1000);
	}
}

export const oktaErrorLoginHandler = () => {
	removeAppCookies();
	const featureFlagData = window.localStorage.getItem("flagFeature");
	const staticPageUrl = isStaticPageHandler();
	window.localStorage.clear();
	if (staticPageUrl && featureFlagData) {
		window.localStorage.setItem("flagFeature", featureFlagData);
	}
	checkSessionHandler();
}

export const checkSessionHandler = async (location) => {
	const staticPageUrl = isStaticPageHandler(location);
	if (staticPageUrl) {
		const isStaticRedirectUrlExist = sessionStorage.getItem(SESSION_STORAGE_KEYS.STATIC_REDIRECT_URL);
		if(!isStaticRedirectUrlExist){
			setPageRedirectUrl(staticPageUrl);
			sessionStorage.setItem(SESSION_STORAGE_KEYS.STATIC_REDIRECT_URL, true);
		}
		oktaWithRedirectHandler();
	}
}

export const getIdentityDelegation = () => {
	const identityDelegation = cacheManager.getItem(USER_INFO)
	  ? get(JSON.parse(cacheManager.getItem(USER_INFO)), IDENTITY_DELEGATION_FLAG)
	  : null;
	if (identityDelegation) {
	  return identityDelegation;
	}
	return null;
};

// TODO : set auth token header.
export const getAuthHeaderToken = async () => {
	const tokenInfo = await OIDC.tokenManager.get('accessToken');
	if (tokenInfo) {
		if (OIDC.tokenManager.hasExpired(tokenInfo)) {
			const renewAccessToken = await OIDC.token.renew(tokenInfo);
			return renewAccessToken ? renewAccessToken.accessToken : null;
		}
		return tokenInfo.accessToken;
	}
	return null;
}


// TODO : Check Session token call on Routes Change
export const verifySessionToken = () => {
	const hashValue = window.location.hash
	if (hashValue) {
		let sessionToken = window.location.hash.split('#')[1]
		OIDC.token.getWithRedirect({
			scopes: OIDC.options.scopes,
			sessionToken
		})
	}
}

// TODO : Render security component from common place.
export const BrowserRoutesProvider = ({ OIDC_CONFIG, ...props }) => {
	const history = useHistory()
	const dispatch = useDispatch();
	const hashValue = window.location.hash;
	const restoreOriginalUri = async (_oktaAuth, originalUri) => {
        history.replace(toRelativeUrl(originalUri || "/", window.location.origin));
    };

	 // https://github.com/okta/okta-auth-js/issues/1365#issuecomment-1387698694
	 React.useEffect(() => {
        (async () => {
            await OIDC.start();
        })();
        return () => {
            (async () => {
                await OIDC.stop();
            })();
        }
    }, []);

	const handleAuth = async () => {
        if (!hashValue) {
            oktaWithRedirectHandler(true);
        }
    };

	React.useEffect(() => {
        if (hashValue) {
            let sessionToken = window.location.hash.split('#')[1];
            if (sessionToken === 'IdentityDelegationRefreshToken') {
                cacheManager.clearSession();
                setPageRedirectUrl('/meeting');
                window.location.replace(window.location.origin);
            } else {
                OIDC.token.getWithRedirect({
                    scopes: OIDC.options.scopes,
                    sessionToken
                });
            }

        }
    }, [hashValue]);

	React.useEffect(() => {
        if(window.localStorage.getItem("flagFeature")) {
			dispatch(setFeatureFlag(true))
		}
    }, []);

    if (hashValue) {
        return null;
    }

	return (
		<Security oktaAuth={OIDC_CONFIG} onAuthRequired={handleAuth} restoreOriginalUri={restoreOriginalUri}>
			<Route path={`${process.env.REACT_APP_MEETING_BASE_URL}/auth`} component={LoginCallback} />
				{props.children}
		</Security>
	)
}

export default {
	OIDC,
	BrowserRoutesProvider,
	getAuthHeaderToken,
	verifySessionToken
}